import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    Link,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material'
import { useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import GoogleSignInButton from '../../../common/GoogleSignInButton'

interface SignUpFormProps {
    form: any
    status: any
    handlers: any
    tHeader: any
    t: any
    onGoogleSignIn: (userData: {
        email: string
        firstName: string
        lastName: string
        fullName: string
        googleId: string
        picture?: string
    }) => void
}

const SignUpForm = ({ form, status, handlers, onGoogleSignIn }: SignUpFormProps) => {
    const pathname = usePathname()
    const locale = pathname.split('/')[1] || 'en'
    const t = useTranslations('common.titles')

    return (
        <Box>
            <GoogleSignInButton onSuccess={onGoogleSignIn} buttonText="Google" fullWidth />
            <Typography align="center" sx={{ mb: 1, color: 'grey.500', fontWeight: 500 }}>
                or
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <FormControl sx={{ minWidth: 80 }}>
                    <InputLabel id="title-select-label">Title</InputLabel>
                    <Select
                        labelId="title-select-label"
                        id="title-select"
                        value={form.title}
                        label="Title"
                        onChange={e => handlers.handlers.setTitle(e.target.value)}
                    >
                        <MenuItem value="Mr">{t('mr')}</MenuItem>
                        <MenuItem value="Ms">{t('ms')}</MenuItem>
                        <MenuItem value="Mrs">{t('mrs')}</MenuItem>
                    </Select>
                </FormControl>
                <TextField
                    label="First Name"
                    fullWidth
                    value={form.firstName}
                    onChange={handlers.handlers.changeFirstNameHandler}
                    error={!form.isValidFirstName}
                    helperText={!form.isValidFirstName ? 'Please enter a valid first name' : ''}
                />
                <TextField
                    label="Last Name"
                    fullWidth
                    value={form.lastName}
                    onChange={handlers.handlers.changeLastNameHandler}
                    error={!form.isValidLastName}
                    helperText={!form.isValidLastName ? 'Please enter a valid last name' : ''}
                />
            </Box>
            <TextField
                fullWidth
                label="Email"
                type="email"
                variant="outlined"
                sx={{ mb: 2 }}
                autoComplete="email"
                value={form.email}
                onChange={handlers.handlers.changeEmailHandler}
                error={!form.isEmailValid}
                helperText={!form.isEmailValid ? 'Please enter a valid email' : ''}
            />
            <TextField
                fullWidth
                label="Password"
                type={form.passwordType ? 'password' : 'text'}
                variant="outlined"
                sx={{ mb: 2 }}
                autoComplete="new-password"
                value={form.password}
                onChange={handlers.handlers.changePasswordHandler}
                error={!form.isPasswordValid}
                helperText={!form.isPasswordValid ? 'Password must be at least 8 characters' : ''}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                onClick={() => handlers.handlers.setPasswordType(!form.passwordType)}
                                edge="end"
                            >
                                {form.passwordType ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    )
                }}
            />
            <Typography variant="caption" sx={{ color: 'grey.600', mb: 2, display: 'block', lineHeight: 1.7 }}>
                By signing up, you agree to our
                <Link
                    href={`/termsOfUse.${locale}.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        fontSize: 12,
                        mx: 0.5,
                        color: 'primary.main',
                        fontWeight: 500,
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline'
                        }
                    }}
                >
                    Terms of Use
                </Link>
                and
                <Link
                    href={`/privacyPolicy.${locale}.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        fontSize: 12,
                        mx: 0.5,
                        color: 'primary.main',
                        fontWeight: 500,
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline'
                        }
                    }}
                >
                    Privacy Policy
                </Link>
            </Typography>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={form.travelCheckedStatus}
                        onChange={e => handlers.handlers.setTravelCheckedStatus(e.target.checked)}
                    />
                }
                label={<Typography sx={{ fontSize: 14 }}>Send me travel deals, promotions, and updates</Typography>}
            />
            <Button
                variant="contained"
                sx={{ width: '100%', mt: 2, fontWeight: 600, fontSize: 16 }}
                onClick={handlers.onSignUp}
                disabled={status.loading}
            >
                {status.loading ? <CircularProgress size={24} color="inherit" /> : 'Sign Up'}
            </Button>
        </Box>
    )
}

export default SignUpForm
